<template>

    <!-- Full-screen loader -->
    <div v-if="loading" class="full-screen-loader">
        <ScaleLoader />
    </div>
    <div v-else class="card">
        <div class="grid">
            <div class="form-field col-12 md:col-6">
                <label for="name">Company Name</label>
                <InputText
                    maxlength = "255"
                    id="name"
                    v-model="company.name"
                    placeholder="Enter company name"
                />
            </div>

            <div class="form-field col-12 md:col-6">
                <label for="type">Type</label>
                <Dropdown id="type" v-model="company.type" optionValue="id" optionLabel="desc" :options="typeValues"
                    placeholder="Select a type" showClear />
            </div>
                

            <div v-if="isGodUser && !companyPreferenceSetupLoading" class="col-12 company-preferences">
                <h6 class="mb-4">
                    Company Preferences:
                </h6>
                <div
                    v-for="setup in generalCompanyPreferenceSetup"
                    class="grid border-top-1 border-gray-200"
                    :key="setup.id"
                >
                    <div class="col-6 align-content-center">
                        {{ setup.description }}
                        <small v-if="parseInt(setup.type) === 20">
                            <br />Paste item or digit them a press <b>Enter</b>
                        </small>
                    </div>
                    <div class="form-field col-4 align-content-center align-self-center">
                        <Dropdown
                            v-if="parseInt(setup.type) === 8"
                            :inputId="setup.code"
                            :options="getOptions(setup.code)"
                            v-model="companyPreference[setup.code]"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select an option"
                            :loading="loadingCompanyPreference"
                        />
                        <Chips
                            v-if="parseInt(setup.type) === 20 && !loadingCompanyPreference"
                            v-model="companyPreference[setup.code]"
                            separator=","
                        />
                        <span v-if="parseInt(setup.type) === 20 && loadingCompanyPreference">
                            Loading...
                        </span>
                    </div>
                    <div class="col-1 align-content-center">
                        <template v-if="parseInt(setup.type) === 8">
                            Default: <b>{{ getSelectedLabel(setup.code, setup.default) }}</b>
                        </template>
                        <template v-else-if="setup.default === '[]'">
                            Empty
                        </template>
                    </div>
                    <div class="col-1 align-content-center">
                        <Button
                            icon="pi pi-refresh"
                            class="p-button-rounded p-button-warning mr-2"
                            @click="resetToDefault(setup.code, setup.default)"
                            v-tooltip="'Reset to default'"
                        />
                    </div>
                </div>

                <!-- Email prefs: any code starting with send_email_ (excluded from general list) -->
                <template v-if="emailCompanyPreferenceSetup.length">
                    <h6 class="mb-3 mt-4">Email Settings</h6>
                    <div
                        v-for="setup in emailCompanyPreferenceSetup"
                        class="grid border-top-1 border-gray-200"
                        :key="'email-' + setup.id"
                    >
                        <div class="col-6 align-content-center">
                            {{ setup.description }}
                            <small v-if="parseInt(setup.type) === 20">
                                <br />Paste item or digit them a press <b>Enter</b>
                            </small>
                        </div>
                        <div class="form-field col-4 align-content-center align-self-center">
                            <Dropdown
                                v-if="parseInt(setup.type) === 8"
                                :inputId="setup.code"
                                :options="getOptions(setup.code)"
                                v-model="companyPreference[setup.code]"
                                optionLabel="label"
                                optionValue="value"
                                placeholder="Select an option"
                                :loading="loadingCompanyPreference"
                            />
                            <Chips
                                v-if="parseInt(setup.type) === 20 && !loadingCompanyPreference"
                                v-model="companyPreference[setup.code]"
                                separator=","
                            />
                            <span v-if="parseInt(setup.type) === 20 && loadingCompanyPreference">
                                Loading...
                            </span>
                        </div>
                        <div class="col-1 align-content-center">
                            <template v-if="parseInt(setup.type) === 8">
                                Default: <b>{{ getSelectedLabel(setup.code, setup.default) }}</b>
                            </template>
                            <template v-else-if="setup.default === '[]'">
                                Empty
                            </template>
                        </div>
                        <div class="col-1 align-content-center">
                            <Button
                                icon="pi pi-refresh"
                                class="p-button-rounded p-button-warning mr-2"
                                @click="resetToDefault(setup.code, setup.default)"
                                v-tooltip="'Reset to default'"
                            />
                        </div>
                    </div>
                </template>

                <!-- Assurance prefs + color box kept together (excluded from list above by code) -->
                <template v-if="assuranceLevelSetup">
                    <h6 class="mb-3 mt-4">Assurance Level Settings</h6>
                    <div class="grid border-top-1 border-gray-200">
                    <div class="col-6 align-content-center">
                        {{ assuranceLevelSetup.description }}
                    </div>
                    <div class="form-field col-4 align-content-center align-self-center">
                        <Dropdown
                            inputId="assurance_level_enabled"
                            :options="getOptions('assurance_level_enabled')"
                            v-model="companyPreference['assurance_level_enabled']"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select an option"
                            :loading="loadingCompanyPreference"
                        />
                    </div>
                    <div class="col-1 align-content-center">
                        Default: <b>{{ getSelectedLabel('assurance_level_enabled', assuranceLevelSetup.default) }}</b>
                    </div>
                    <div class="col-1 align-content-center">
                        <Button
                            icon="pi pi-refresh"
                            class="p-button-rounded p-button-warning mr-2"
                            @click="resetToDefault('assurance_level_enabled', assuranceLevelSetup.default)"
                            v-tooltip="'Reset to default'"
                        />
                    </div>
                    </div>

                    <div
                        v-if="assuranceColorsSetup && isAssuranceLevelEnabled"
                        class="grid border-top-1 border-gray-200 w-full"
                    >
                        <div class="col-12 mt-2 mb-2">
                            <div class="mb-2 text-sm text-gray-700">
                                {{ assuranceColorsSetup.description }}
                            </div>
                            <div class="ac-section">
                                <div class="ac-colors-row">
                                    <div v-for="level in assuranceLevels" :key="level.id" class="ac-color-item">
                                        <span class="ac-color-item__label">{{ level.label }}</span>
                                        <InputText
                                            v-model="assuranceColors[level.id].label"
                                            :inputId="'text-' + level.id"
                                            class="ac-color-item__text-input"
                                            placeholder="Label text"
                                        />
                                        <ColorPicker
                                            v-model="assuranceColors[level.id].color"
                                            :inputId="'color-' + level.id"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
            </div>
            <!-- SSO Entra: enable toggle + configuration kept together -->
            <div
                v-if="isGodUser && !companyPreferenceSetupLoading && ssoEntraEnabledSetup"
                class="col-12 company-preferences"
            >
                <h6 class="mb-3">SSO Entra Configuration</h6>
                <div class="grid border-top-1 border-gray-200 w-full">
                    <div class="col-6 align-content-center">
                        {{ ssoEntraEnabledSetup.description }}
                    </div>
                    <div class="form-field col-4 align-content-center align-self-center">
                        <Dropdown
                            inputId="sso_entra_enabled"
                            :options="getOptions('sso_entra_enabled')"
                            v-model="companyPreference['sso_entra_enabled']"
                            optionLabel="label"
                            optionValue="value"
                            placeholder="Select an option"
                            :loading="loadingCompanyPreference"
                        />
                    </div>
                    <div class="col-1 align-content-center">
                        Default: <b>{{ getSelectedLabel('sso_entra_enabled', ssoEntraEnabledSetup.default) }}</b>
                    </div>
                    <div class="col-1 align-content-center">
                        <Button
                            icon="pi pi-refresh"
                            class="p-button-rounded p-button-warning mr-2"
                            @click="resetToDefault('sso_entra_enabled', ssoEntraEnabledSetup.default)"
                            v-tooltip="'Reset to default'"
                        />
                    </div>
                </div>
                <div v-if="showEntraForm" class="grid w-full mt-3">
                    <div class="form-field col-12 md:col-3">
                        <label for="entra-secret">Entra Secret Value</label>
                        <InputText
                            id="entra-secret"
                            v-model="company.entra_client_secret"
                            placeholder="Enter entra secret value"
                            type="password"
                            maxlength="255"
                        />
                    </div>
                    <div class="form-field col-12 md:col-3">
                        <label for="entra-tenant">Entra Tenant ID</label>
                        <InputText
                            id="entra-tenant"
                            v-model="company.entra_tenant_id"
                            placeholder="Enter entra tenant ID"
                            maxlength="255"
                        />
                    </div>
                    <div class="form-field col-12 md:col-3">
                        <label for="entra-client">Entra Client ID</label>
                        <InputText
                            id="entra-client"
                            v-model="company.entra_client_id"
                            placeholder="Enter entra client ID"
                            maxlength="255"
                        />
                    </div>
                    <div class="form-field col-12 md:col-3">
                        <label for="entra-domain">Entra Domain Names</label>
                        <InputText
                            id="entra-domain"
                            v-model="company.entra_domain_names"
                            placeholder="Enter entra domain names"
                            maxlength="255"
                        />
                    </div>
                    <div class="form-field col-12 md:col-3">
                        <h6 class="mb-2">Azure Redirect AMS URI:</h6>
                        <p class="text-sm text-gray-600">{{ company.azure_redirect_ams_uri || 'Not configured' }}</p>
                    </div>
                    <div class="form-field col-12 md:col-3">
                        <h6 class="mb-2">Azure Redirect PHA URI:</h6>
                        <p class="text-sm text-gray-600">{{ company.azure_redirect_pha_uri || 'Not configured' }}</p>
                    </div>
                </div>
            </div>

            <div v-if="showThemeHelpLinkInput" class="form-field col-12 flex flex-wrap">
                <h6 class="mb-4">Theme Help Resource Link:</h6>
                <div class="grid w-full">
                    <div class="form-field col-12 md:col-6">
                        <label for="theme-help-link">Theme Help Link</label>
                        <InputText id="theme-help-link" v-model="companyPreference['theme_help_link']" />
                    </div>
                </div>
            </div>

            <div class="form-field col-12 flex flex-wrap mb-3">
                <div class="flex justify-content-end">
                    <Button
                        label="Cancel"
                        :loading="loading"
                        icon="pi pi-times"
                        class="p-button-danger"
                        @click="onCancel"/>
                    <Button
                        :disabled="invalid"
                        label="Update"
                        :loading="loading"
                        icon="pi pi-check"
                        class="ml-2 p-button-primary"
                        @click="onSubmit" />
                </div>
            </div>
        </div>



    </div>
</template>

<script setup>

import { computed, defineProps, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import GlobalRequest from '@/api/global-request';
import { useStore } from 'vuex';
import { showToast } from '@/helpers/toast-handler';


const router = useRouter();
const store = useStore();

const company = ref({
    name: '',
    id: null,
    type: null,
    logo_id: null, // file id
    uses_entra: 0,
    entra_client_id: '',
    entra_client_secret: '',
    entra_tenant_id: '',
    entra_domain_names: '',
    azure_redirect_ams_uri: '',
    azure_redirect_pha_uri: ''
})
const loading = ref(false);
const companyPreferenceSetup = ref([])
const companyPreferenceSetupLoading = ref(false);
const companyPreference = ref({})
const loadingCompanyPreference = ref(false)


const assuranceLevels = [
    { id: 'inadequate', label: 'Inadequate (0-19%)', defaultColor: '#EF4444' },
    { id: 'requires_improvement', label: 'Requires Improvement (20-49%)', defaultColor: '#B91C1C' },
    { id: 'adequate', label: 'Adequate (50-69%)', defaultColor: '#F97316' },
    { id: 'good', label: 'Good (70-89%)', defaultColor: '#22C55E' },
    { id: 'outstanding', label: 'Outstanding (90-100%)', defaultColor: '#166534' },
];

const assuranceColors = ref({});

assuranceLevels.forEach(level => {
    assuranceColors.value[level.id] = { color: level.defaultColor, label: level.label };
});

const invalid = computed(() => {
    if (company.value.name === null || company.value.name.length < 1) {
        return true;
    } else if (company.value.type === null || company.value.type === undefined || company.value.type === '') {
        return true;
    } else {
        return false
    }
})
const isGodUser = computed(() => {
    return store.getters['auth/isGodUser'];
});

const showEntraForm = computed(() => {
    return isGodUser.value && 
           (companyPreference.value['sso_entra_enabled'] === 1 || 
            companyPreference.value['sso_entra_enabled'] === '1');
});

const showThemeHelpLinkInput = computed(() => {
    return isGodUser.value &&
           (companyPreference.value['enable_theme_help_resource_links'] === 1 ||
            companyPreference.value['enable_theme_help_resource_links'] === '1');
});

const ASSURANCE_PREF_CODES = ['assurance_level_enabled', 'assurance_colors_data'];
const SSO_PREF_CODES = ['sso_entra_enabled'];

/** Backend email toggles use preference codes starting with send_email_ */
function isEmailPreferenceCode(code) {
    return typeof code === 'string' && code.startsWith('send_email_');
}

/** Master task-email switch — show first in the email section (not A–Z order). */
const EMAIL_PREF_MASTER_TASK_SWITCH = 'send_email_tasks_scheduled';

const generalCompanyPreferenceSetup = computed(() => {
    return (companyPreferenceSetup.value ?? []).filter(
        (setup) =>
            !ASSURANCE_PREF_CODES.includes(setup.code) &&
            !SSO_PREF_CODES.includes(setup.code) &&
            !isEmailPreferenceCode(setup.code)
    );
});

const ssoEntraEnabledSetup = computed(() => {
    return (companyPreferenceSetup.value ?? []).find(
        (setup) => setup.code === 'sso_entra_enabled'
    );
});

const emailCompanyPreferenceSetup = computed(() => {
    return (companyPreferenceSetup.value ?? [])
        .filter((setup) => isEmailPreferenceCode(setup.code))
        .slice()
        .sort((a, b) => {
            // Master task-email switch always first; rest alphabetical by code
            if (a.code === EMAIL_PREF_MASTER_TASK_SWITCH) return -1;
            if (b.code === EMAIL_PREF_MASTER_TASK_SWITCH) return 1;
            return a.code.localeCompare(b.code);
        });
});

const assuranceLevelSetup = computed(() => {
    return companyPreferenceSetup.value.find(
        (setup) => setup.code === 'assurance_level_enabled'
    );
});

const assuranceColorsSetup = computed(() => {
    return companyPreferenceSetup.value.find(
        (setup) => setup.code === 'assurance_colors_data'
    );
});

const isAssuranceLevelEnabled = computed(() => {
    const value = companyPreference.value['assurance_level_enabled'];
    return value === 1 || value === '1';
});

const typeValues = [
    {
        id: 1,
        desc: 'FIRE AND RESCUE',
    },
    {
        id: 2,
        desc: 'POLICE',
    },
    {
        id: 3,
        desc: 'NHS',
    },
    {
        id: 4,
        desc: 'RESIDENTIAL HEALTHCARE',
    },
    {
        id: 5,
        desc: 'COMMERCIAL',
    },
]


const props = defineProps({
    id: {
        type: [String, Number],
        required: true,
    },
});

// Watch for changes to the sso_entra_enabled preference
watch(() => companyPreference.value['sso_entra_enabled'], (newValue, oldValue) => {
    console.log('sso_entra_enabled preference changed:', { oldValue, newValue, type: typeof newValue });
    console.log('After change - showEntraForm should be:', (newValue === 1 || newValue === '1'));
})

onMounted(() => {

    if (isGodUser.value) {
        loadCompanyPreferenceSetup()
    }

    if (typeof props.id !== 'undefined' && props.id !== null) {
        loadCompany(props.id)
    }
})

function resetToDefault(code, defaultValue) {
    companyPreference.value[code] = defaultValue
}

function getOptions(code) {
    const data = companyPreferenceSetup.value?.find((setup) => {
        return setup.code === code
    })

    return data?.options ?? []
}

function getSelectedLabel(code, value) {
    const options = getOptions(code)

    let selected = ''

    if (!options?.length) {
        return selected
    }

    options.forEach((option) => {
        if (String(option.value) === String(value)) {
            selected = option.label
        }
    })

    return selected
}

function loadCompany(id) {
    loading.value = true
    loadingCompanyPreference.value = true

    GlobalRequest.getRequest(
        `v2/admin/companies/${id}`,
    ).then((response) => {
        if (Object.hasOwn(response, 'data')) {
            company.value.id = response.data.id;
            company.value.name = response.data.name;
            company.value.type = response.data.type;
            // company.value.uses_entra = response.data.uses_entra || 0;
            company.value.entra_client_id = response.data.entra_client_id || '';
            company.value.entra_client_secret = response.data.entra_client_secret || '';
            company.value.entra_tenant_id = response.data.entra_tenant_id || '';
            company.value.entra_domain_names = response.data.entra_domain_names || '';
            company.value.azure_redirect_ams_uri = response.data.azure_redirect_ams_uri || '';
            company.value.azure_redirect_pha_uri = response.data.azure_redirect_pha_uri || '';
             logoPreview.value = response.data.logo?.vignette
            ? response.data.logo.vignette   // base64 thumbnail from API
            : response.data.logo?.thumbnail ;     // fallback: S3 URI or local path

            if (company.value.id !== null) {
                GlobalRequest.getRequest(
                    `/v2/admin/companies/preference/${company.value.id}`
                ).then((response) => {
                    const prefs = response?.data
                    if (!prefs || typeof prefs !== 'object') {
                        return
                    }

                    Object.entries(prefs).forEach(([key, value]) => {
                        if (key === 'protected_attributes') {
                            if (value === '[]' || value === null || value === undefined) {
                                value = ''
                            }
                            companyPreference.value[key] = value
                            return
                        }

                        // Unset / null prefs must not call .toString() (prod crash)
                        if (value === null || value === undefined) {
                            return
                        }

                        companyPreference.value[key] = value.toString()

                        if (key === 'assurance_colors_data' && value) {
                            try {
                                const parsed = typeof value === 'string' ? JSON.parse(value) : value
                                if (!parsed || typeof parsed !== 'object') {
                                    return
                                }
                                // Handle both old (flat hex) and new ({color, label}) formats
                                Object.keys(parsed).forEach((levelId) => {
                                    if (!assuranceColors.value[levelId]) {
                                        assuranceColors.value[levelId] = {
                                            color: '#000000',
                                            label: levelId,
                                        }
                                    }
                                    if (typeof parsed[levelId] === 'object' && parsed[levelId] !== null) {
                                        assuranceColors.value[levelId] = {
                                            ...assuranceColors.value[levelId],
                                            ...parsed[levelId],
                                        }
                                    } else {
                                        // Old format: plain hex string — keep existing label
                                        assuranceColors.value[levelId].color = parsed[levelId]
                                    }
                                })
                            } catch (e) { /* keep defaults */ }
                        }
                    })
                }).finally(() => {
                    loadingCompanyPreference.value = false
                })

                loadThemeHelpLink(company.value.id)
            }
        }
    }).finally(() => {
        loading.value = false
    })
}

function loadThemeHelpLink(companyId) {
    GlobalRequest.getRequest(
        `/v2/admin/company/${companyId}/theme-help-link`
    ).then((response) => {
        const link = response.data?.value

        companyPreference.value['theme_help_link'] = (link && link !== '[]') ? link : ''
    }).catch(() => {
        companyPreference.value['theme_help_link'] = ''
    })
}

function loadCompanyPreferenceSetup() {
    companyPreferenceSetupLoading.value = true
    GlobalRequest.getRequest(
        `/v2/admin/companies/preference/setup`,
    ).then((response) => {

        if (Object.hasOwn(response, 'data')) {

            response.data.forEach((setup) => {
                let options = setup.options
                if (typeof options === 'string') {
                    try {
                        options = JSON.parse(options)
                    } catch (e) {
                        options = []
                    }
                }
                if (!Array.isArray(options)) {
                    options = []
                }

                options.forEach((option) => {
                    if (option && typeof option.value === 'number') {
                        option.value = option.value.toString()
                    }
                })

                // Dropdown optionValues are strings — keep default as string for type 8
                let defaultValue = setup.default
                if (parseInt(setup.type) === 8 && defaultValue !== null && defaultValue !== undefined) {
                    defaultValue = defaultValue.toString()
                }

                companyPreferenceSetup.value.push({
                    id: setup.id,
                    code: setup.code,
                    default: defaultValue,
                    description: setup.description,
                    options,
                    type: setup.type,
                })

                companyPreference.value[setup.code] = defaultValue
            })
        }
    }).finally(() => {
        companyPreferenceSetupLoading.value = false
    })
}

function validateEntraConfig() {
    // Check if SSO Entra is enabled
    if (companyPreference.value['sso_entra_enabled'] === 1 || companyPreference.value['sso_entra_enabled'] === '1') {
        const missingFields = []
        
        if (!company.value.entra_client_secret?.trim()) {
            missingFields.push('Entra Secret Value')
        }
        if (!company.value.entra_tenant_id?.trim()) {
            missingFields.push('Entra Tenant ID')
        }
        if (!company.value.entra_client_id?.trim()) {
            missingFields.push('Entra Client ID')
        }
        if (!company.value.entra_domain_names?.trim()) {
            missingFields.push('Entra Domain Names')
        }
        
        if (missingFields.length > 0) {
            const message = `SSO Entra configuration incomplete: ${missingFields.join(', ')} cannot be empty`
            showToast({}, message)
            return false
        }
    }
    
    return true
}

function onSubmit() {
    // Validate Entra config before proceeding
    if (!validateEntraConfig()) {
        return
    }
    
    loading.value = true
    if (company.value.id !== null) {
        GlobalRequest.patchRequest(
            `v2/admin/companies/${company.value.id}`, company.value
        ).then((response) => {
            saveCompanyPreferences(response.data.id).then(() => {
                loading.value = false
                router.go(-1)
            })
        })
    } else {
        GlobalRequest.postRequest(
            `v2/admin/companies`, company.value
        ).then((response) => {
            saveCompanyPreferences(response.data.id).then(() => {
                loading.value = false
                router.go(-1)
            })
        })
    }
}

function saveCompanyPreferences(companyId) {
    let requests = []

    // Always save assurance colors data
    const colorData = JSON.stringify(assuranceColors.value);
    requests.push(GlobalRequest.postRequest(
        `/v2/admin/companies/preference`, {
            company_id: companyId,
            preference_code: 'assurance_colors_data',
            value: colorData,
        }
    ))

    Object.entries(companyPreference.value).forEach(([key, value]) => {
        // Skip assurance_colors_data -- handled separately above
        if (key === 'assurance_colors_data') return;

        if (key === 'protected_attributes') {
            if (value === null) {
                value = []
            }

            value = JSON.stringify(value);
        }

        requests.push(GlobalRequest.postRequest(
            `/v2/admin/companies/preference`, {
                company_id: companyId,
                preference_code: key,
                value: value,
            }
        ))
    })


    return Promise.all(requests)
}

function onCancel() {
    router.go(-1)
}

const logoPreview = ref(''); // Base64 or S3 preview URL



</script>

<style lang="scss" scoped>
.form-field {
    display: flex;
    flex-direction: column;
    margin-bottom: 0.5rem;
}

.form-field label {
    margin-bottom: 0.5rem;
}

.logo-preview {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}

.full-screen-loader {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 9999; // on top of everything
}

/* Optional: keep existing styles */
.logo-preview {
  width: 100px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}



.company-preferences {
  width: 100%;
}

/* Assurance Level Colors */
.ac-section {
  border: 1px solid var(--surface-border, #e5e7eb);
  border-left: 3px solid #6366f1;
  border-radius: 6px;
  background: var(--surface-card, #fff);
  padding: 0.6rem 1rem;
  width: fit-content;
  margin: 0 auto;
}

.ac-colors-row {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(290px, 1fr));
  gap: 0.5rem 1rem;
}

.ac-color-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
}

.ac-color-item__label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #374151;
  width: 200px;
  flex-shrink: 0;
}

.ac-color-item__text-input {
  width: 20rem;
  font-size: 0.75rem;
}
</style>